import type { Piece, PieceColor, PieceType, Board, ChessGame } from "./types.js";
import { SIZE, EMPTY, PIECE_VALUES } from "./types.js";

function makePiece(type: PieceType, color: PieceColor): Piece {
  return { type, color };
}

function opponent(c: PieceColor): PieceColor {
  return c === "white" ? "black" : "white";
}

function cloneBoard(b: Board): Board {
  return b.map((row) => [...row]);
}

function findKing(board: Board, color: PieceColor): { r: number; c: number } | null {
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE; c++) {
      const p = board[r][c];
      if (p && p.type === "king" && p.color === color) return { r, c };
    }
  return null;
}

function inBounds(r: number, c: number): boolean {
  return r >= 0 && r < SIZE && c >= 0 && c < SIZE;
}

function enemyAt(board: Board, r: number, c: number, color: PieceColor): boolean {
  const p = board[r][c];
  return !!p && p.color !== color;
}

function emptyAt(board: Board, r: number, c: number): boolean {
  return !board[r][c];
}

function rayMoves(board: Board, r: number, c: number, dirs: [number, number][]): { r: number; c: number }[] {
  const moves: { r: number; c: number }[] = [];
  const color = board[r][c]!.color;
  for (const [dr, dc] of dirs) {
    let nr = r + dr, nc = c + dc;
    while (inBounds(nr, nc)) {
      if (emptyAt(board, nr, nc)) {
        moves.push({ r: nr, c: nc });
      } else {
        if (enemyAt(board, nr, nc, color)) moves.push({ r: nr, c: nc });
        break;
      }
      nr += dr;
      nc += dc;
    }
  }
  return moves;
}

export function pseudoMoves(board: Board, r: number, c: number): { r: number; c: number }[] {
  const piece = board[r][c];
  if (!piece) return [];
  const { type, color } = piece;
  const enemy = (nr: number, nc: number) => enemyAt(board, nr, nc, color);

  switch (type) {
    case "pawn": {
      const moves: { r: number; c: number }[] = [];
      const dir = color === "white" ? -1 : 1;
      const startRow = color === "white" ? 6 : 1;
      if (inBounds(r + dir, c) && emptyAt(board, r + dir, c)) {
        moves.push({ r: r + dir, c });
        if (r === startRow && emptyAt(board, r + 2 * dir, c)) moves.push({ r: r + 2 * dir, c });
      }
      for (const dc of [-1, 1]) {
        if (inBounds(r + dir, c + dc) && enemy(r + dir, c + dc)) moves.push({ r: r + dir, c: c + dc });
      }
      return moves;
    }
    case "knight": {
      const deltas: [number, number][] = [[-2,-1],[-2,1],[-1,-2],[-1,2],[1,-2],[1,2],[2,-1],[2,1]];
      const moves: { r: number; c: number }[] = [];
      for (const [dr, dc] of deltas) {
        const nr = r + dr, nc = c + dc;
        if (inBounds(nr, nc) && (emptyAt(board, nr, nc) || enemy(nr, nc))) moves.push({ r: nr, c: nc });
      }
      return moves;
    }
    case "bishop":
      return rayMoves(board, r, c, [[-1,-1],[-1,1],[1,-1],[1,1]]);
    case "rook":
      return rayMoves(board, r, c, [[-1,0],[1,0],[0,-1],[0,1]]);
    case "queen":
      return rayMoves(board, r, c, [[-1,-1],[-1,0],[-1,1],[0,-1],[0,1],[1,-1],[1,0],[1,1]]);
    case "king": {
      const moves: { r: number; c: number }[] = [];
      for (let dr = -1; dr <= 1; dr++)
        for (let dc = -1; dc <= 1; dc++) {
          if (dr === 0 && dc === 0) continue;
          const nr = r + dr, nc = c + dc;
          if (inBounds(nr, nc) && (emptyAt(board, nr, nc) || enemy(nr, nc))) moves.push({ r: nr, c: nc });
        }
      return moves;
    }
  }
  return [];
}

function isSquareAttacked(board: Board, r: number, c: number, by: PieceColor): boolean {
  for (let sr = 0; sr < SIZE; sr++)
    for (let sc = 0; sc < SIZE; sc++) {
      const p = board[sr][sc];
      if (!p || p.color !== by) continue;
      const m = pseudoMoves(board, sr, sc);
      if (m.some((mv) => mv.r === r && mv.c === c)) return true;
    }
  return false;
}

function isInCheck(board: Board, color: PieceColor): boolean {
  const king = findKing(board, color);
  if (!king) return false;
  return isSquareAttacked(board, king.r, king.c, opponent(color));
}

function simulateMove(board: Board, fr: number, fc: number, tr: number, tc: number): Board {
  const b = cloneBoard(board);
  const piece = b[fr][fc];
  b[fr][fc] = EMPTY;
  b[tr][tc] = piece;
  if (piece && piece.type === "pawn" && (tr === 0 || tr === SIZE - 1)) {
    b[tr][tc] = { type: "queen", color: piece.color };
  }
  return b;
}

export function legalMoves(board: Board, r: number, c: number): { r: number; c: number }[] {
  const piece = board[r][c];
  if (!piece) return [];
  const all = pseudoMoves(board, r, c);
  return all.filter((mv) => {
    const newBoard = simulateMove(board, r, c, mv.r, mv.c);
    return !isInCheck(newBoard, piece.color);
  });
}

function hasLegalMoves(board: Board, color: PieceColor): boolean {
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE; c++) {
      const p = board[r][c];
      if (p && p.color === color && legalMoves(board, r, c).length > 0) return true;
    }
  return false;
}

export function createGame(): ChessGame {
  const board: Board = Array.from({ length: SIZE }, () => Array(SIZE).fill(EMPTY));
  const backRow: PieceType[] = ["rook", "knight", "bishop", "queen", "king", "bishop", "knight", "rook"];
  for (let c = 0; c < SIZE; c++) {
    board[0][c] = makePiece(backRow[c], "black");
    board[1][c] = makePiece("pawn", "black");
    board[6][c] = makePiece("pawn", "white");
    board[7][c] = makePiece(backRow[c], "white");
  }
  return {
    board, turn: "white", winner: null, isDraw: false,
    selected: null, validMoves: [], inCheck: false, lastMove: null, moveCount: 0, halfMoveClock: 0,
  };
}

function cloneGame(g: ChessGame): ChessGame {
  return {
    ...g,
    board: cloneBoard(g.board),
    validMoves: [...g.validMoves],
    selected: g.selected ? { ...g.selected } : null,
    lastMove: g.lastMove ? { from: { ...g.lastMove.from }, to: { ...g.lastMove.to } } : null,
  };
}

function applyMove(game: ChessGame, fr: number, fc: number, tr: number, tc: number): ChessGame {
  const piece = game.board[fr][fc];
  if (!piece) return game;
  const captured = game.board[tr][tc];
  const newBoard = simulateMove(game.board, fr, fc, tr, tc);
  const nextTurn = opponent(game.turn);
  const inCheck = isInCheck(newBoard, nextTurn);
  const hasLegal = hasLegalMoves(newBoard, nextTurn);
  let winner: PieceColor | null = null;
  let isDraw = false;
  if (!hasLegal) {
    if (inCheck) winner = game.turn;
    else isDraw = true;
  }
  const halfMove = (piece.type === "pawn" || captured) ? 0 : game.halfMoveClock + 1;
  if (halfMove >= 100) isDraw = true;
  return {
    board: newBoard, turn: nextTurn, winner, isDraw,
    selected: null, validMoves: [], inCheck,
    lastMove: { from: { r: fr, c: fc }, to: { r: tr, c: tc } },
    moveCount: game.moveCount + 1, halfMoveClock: halfMove,
  };
}

export function selectCell(game: ChessGame, r: number, c: number): ChessGame {
  if (game.winner || game.isDraw) return game;
  const piece = game.board[r][c];
  if (piece && piece.color === game.turn) {
    const moves = legalMoves(game.board, r, c);
    return { ...game, selected: { r, c }, validMoves: moves };
  }
  if (game.selected) {
    const move = game.validMoves.find((m) => m.r === r && m.c === c);
    if (move) return applyMove(cloneGame(game), game.selected.r, game.selected.c, r, c);
    return { ...game, selected: null, validMoves: [] };
  }
  return game;
}

// ---- AI -------------------------------------------------------------------

const POSITION_TABLES: Record<string, number[][]> = {
  pawn: [
    [0,0,0,0,0,0,0,0],[50,50,50,50,50,50,50,50],[10,10,20,30,30,20,10,10],[5,5,10,25,25,10,5,5],
    [0,0,0,20,20,0,0,0],[5,-5,-10,0,0,-10,-5,5],[5,10,10,-20,-20,10,10,5],[0,0,0,0,0,0,0,0],
  ],
  knight: [
    [-50,-40,-30,-30,-30,-30,-40,-50],[-40,-20,0,0,0,0,-20,-40],[-30,0,10,15,15,10,0,-30],
    [-30,5,15,20,20,15,5,-30],[-30,0,15,20,20,15,0,-30],[-30,5,10,15,15,10,5,-30],
    [-40,-20,0,5,5,0,-20,-40],[-50,-40,-30,-30,-30,-30,-40,-50],
  ],
  bishop: [
    [-20,-10,-10,-10,-10,-10,-10,-20],[-10,0,0,0,0,0,0,-10],[-10,0,5,10,10,5,0,-10],
    [-10,5,5,10,10,5,5,-10],[-10,0,5,10,10,5,0,-10],[-10,10,10,10,10,10,10,-10],
    [-10,5,0,0,0,0,5,-10],[-20,-10,-10,-10,-10,-10,-10,-20],
  ],
  rook: [
    [0,0,0,0,0,0,0,0],[5,10,10,10,10,10,10,5],[-5,0,0,0,0,0,0,-5],
    [-5,0,0,0,0,0,0,-5],[-5,0,0,0,0,0,0,-5],[-5,0,0,0,0,0,0,-5],
    [-5,0,0,0,0,0,0,-5],[0,0,0,5,5,0,0,0],
  ],
  queen: [
    [-20,-10,-10,-5,-5,-10,-10,-20],[-10,0,0,0,0,0,0,-10],[-10,0,5,5,5,5,0,-10],
    [-5,0,5,5,5,5,0,-5],[0,0,5,5,5,5,0,-5],[-10,5,5,5,5,5,0,-10],
    [-10,0,5,0,0,0,0,-10],[-20,-10,-10,-5,-5,-10,-10,-20],
  ],
  king: [
    [-30,-40,-40,-50,-50,-40,-40,-30],[-30,-40,-40,-50,-50,-40,-40,-30],[-30,-40,-40,-50,-50,-40,-40,-30],
    [-30,-40,-40,-50,-50,-40,-40,-30],[-20,-30,-30,-40,-40,-30,-30,-20],[-10,-20,-20,-20,-20,-20,-20,-10],
    [20,20,0,0,0,0,20,20],[20,30,10,0,0,10,30,20],
  ],
};

function evaluateBoard(board: Board): number {
  let score = 0;
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE; c++) {
      const p = board[r][c];
      if (!p) continue;
      const table = POSITION_TABLES[p.type];
      const pr = p.color === "white" ? r : SIZE - 1 - r;
      const posVal = table ? (table[pr]?.[c] ?? 0) : 0;
      score += p.color === "white" ? PIECE_VALUES[p.type] + posVal : -(PIECE_VALUES[p.type] + posVal);
    }
  return score;
}

function enumerateMoves(game: ChessGame): Array<{ fr: number; fc: number; tr: number; tc: number; result: ChessGame }> {
  const moves: Array<{ fr: number; fc: number; tr: number; tc: number; result: ChessGame }> = [];
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE; c++) {
      const p = game.board[r][c];
      if (!p || p.color !== game.turn) continue;
      for (const mv of legalMoves(game.board, r, c)) {
        moves.push({ fr: r, fc: c, tr: mv.r, tc: mv.c, result: applyMove(cloneGame(game), r, c, mv.r, mv.c) });
      }
    }
  return moves;
}

function minimax(game: ChessGame, depth: number, maximizing: boolean, alpha: number, beta: number): number {
  if (game.winner === "white") return 100000 + depth;
  if (game.winner === "black") return -(100000 + depth);
  if (game.isDraw || depth === 0) return evaluateBoard(game.board);
  const moves = enumerateMoves(game);
  if (moves.length === 0) return maximizing ? -(100000 + depth) : 100000 + depth;

  if (maximizing) {
    let maxEval = -Infinity;
    for (const move of moves) {
      const ev = minimax(move.result, depth - 1, false, alpha, beta);
      maxEval = Math.max(maxEval, ev);
      alpha = Math.max(alpha, ev);
      if (beta <= alpha) break;
    }
    return maxEval;
  } else {
    let minEval = Infinity;
    for (const move of moves) {
      const ev = minimax(move.result, depth - 1, true, alpha, beta);
      minEval = Math.min(minEval, ev);
      beta = Math.min(beta, ev);
      if (beta <= alpha) break;
    }
    return minEval;
  }
}

export function getAIMove(game: ChessGame): { fr: number; fc: number; tr: number; tc: number } | null {
  const moves = enumerateMoves(game);
  if (moves.length === 0) return null;
  const depth = game.moveCount < 10 ? 3 : 2;
  let bestScore = Infinity;
  let bestMove = moves[0];
  for (const move of moves) {
    const score = minimax(move.result, depth - 1, true, -Infinity, Infinity);
    if (score < bestScore) {
      bestScore = score;
      bestMove = move;
    }
  }
  return { fr: bestMove.fr, fc: bestMove.fc, tr: bestMove.tr, tc: bestMove.tc };
}
